/* eslint-disable no-bitwise -- protobuf wire format 은 비트 연산이 본질(varint/tag) */
// Google Authenticator "계정 이전" QR 디코더(PR7 #1514).
//
// otpauth-migration://offline?data=<base64(protobuf MigrationPayload)> 를 개별
// otpauth:// URI 목록으로 푼다. 파싱은 전부 클라이언트(제로지식 — 평문 secret 은
// 서버로 절대 전송하지 않으며, 이 모듈은 로그/에러에 secret·payload 를 남기지 않는다).
//
// protobuf 파서 라이브러리들은 전부 protobufjs(3.7MB)를 끌고 와 기각(설계 §2.2) —
// 스키마가 varint/length-delimited 두 wire type 만 쓰는 소형이라 수동 디코딩한다.
// 스키마(공개 리버스엔지니어링 자료 검증 — 설계 문서 참조):
//   MigrationPayload: otp_parameters(1, repeated) / version(2) / batch_size(3) /
//                     batch_index(4) / batch_id(5)
//   OtpParameters:    secret bytes(1) / name(2) / issuer(3) / algorithm(4) /
//                     digits(5) / type(6) / counter(7)
// 알 수 없는 필드는 skip — Google 이 필드를 추가해도 파싱이 깨지지 않게(전방 호환).
import * as OTPAuth from 'otpauth'

const MIGRATION_SCHEME = 'otpauth-migration://'

function migrationError(code) {
  const e = new Error(code)
  e.code = code
  return e
}

// ── protobuf wire 리더 ──
// varint 값은 batch/enum/counter 용도라 Number 범위로 충분(53비트 초과는 이 도메인에
// 등장하지 않음 — 등장해도 값 자체는 미사용 필드).
function readVarint(bytes, pos) {
  let value = 0
  let shift = 1
  let p = pos
  for (;;) {
    if (p >= bytes.length) throw migrationError('INVALID_MIGRATION_PAYLOAD')
    const b = bytes[p]
    p += 1
    value += (b & 0x7f) * shift
    if ((b & 0x80) === 0) return { value, pos: p }
    shift *= 128
  }
}

// 메시지 바이트를 {fieldNo: [값…]} 로 푼다. LD 필드는 Uint8Array, varint 는 Number.
function readMessageFields(bytes) {
  const fields = {}
  let pos = 0
  while (pos < bytes.length) {
    const tag = readVarint(bytes, pos)
    pos = tag.pos
    const fieldNo = Math.floor(tag.value / 8)
    const wireType = tag.value % 8

    let value
    if (wireType === 0) {
      const v = readVarint(bytes, pos)
      value = v.value
      pos = v.pos
    } else if (wireType === 2) {
      const len = readVarint(bytes, pos)
      pos = len.pos
      if (pos + len.value > bytes.length) throw migrationError('INVALID_MIGRATION_PAYLOAD')
      value = bytes.subarray(pos, pos + len.value)
      pos += len.value
    } else if (wireType === 1 || wireType === 5) {
      // fixed64/fixed32 — 이 스키마엔 없지만 길이가 고정이라 안전하게 skip 가능
      // (전방 호환: Google 이 고정폭 필드를 추가해도 QR 전체가 깨지지 않게 — PR 리뷰 수용)
      const width = wireType === 1 ? 8 : 4
      if (pos + width > bytes.length) throw migrationError('INVALID_MIGRATION_PAYLOAD')
      value = null // 미지 필드 — 값 미사용
      pos += width
    } else {
      // 남는 wire type(3/4 group — deprecated)은 길이를 알 수 없어 중단
      throw migrationError('INVALID_MIGRATION_PAYLOAD')
    }

    if (!fields[fieldNo]) fields[fieldNo] = []
    fields[fieldNo].push(value)
  }
  return fields
}

const textDecoder = new TextDecoder()
const ALGORITHMS = { 0: 'SHA1', 1: 'SHA1', 2: 'SHA256', 3: 'SHA512' } // 4=MD5 미지원
const DIGITS = { 0: 6, 1: 6, 2: 8 }
const OTP_TYPE_TOTP = 2

function toAccount(paramBytes) {
  const f = readMessageFields(paramBytes)
  const secretBytes = f[1]?.[0]
  // 조작된 payload 가 string 필드를 varint 로 인코딩하면 Number 가 올 수 있다 — 타입 가드(PR 리뷰)
  const name = f[2]?.[0] instanceof Uint8Array ? textDecoder.decode(f[2][0]) : ''
  const issuer = f[3]?.[0] instanceof Uint8Array ? textDecoder.decode(f[3][0]) : ''
  const algorithm = f[4]?.[0] ?? 0
  const digits = f[5]?.[0] ?? 0
  const type = f[6]?.[0] ?? 0

  const base = { issuer, name }

  if (type !== OTP_TYPE_TOTP) return { ...base, skipped: 'hotp' }
  if (!(secretBytes instanceof Uint8Array) || secretBytes.length === 0) {
    return { ...base, skipped: 'invalid' }
  }
  if (ALGORITHMS[algorithm] == null) return { ...base, skipped: 'invalid' } // MD5 등

  // Uint8Array 는 버퍼 풀/서브어레이일 수 있어 정확 범위 slice 필수(설계 §2.4 실측 함정
  // — .buffer 를 그대로 주면 base32 가 풀 전체로 오염된다)
  const secret = new OTPAuth.Secret({
    buffer: secretBytes.buffer.slice(
      secretBytes.byteOffset,
      secretBytes.byteOffset + secretBytes.byteLength,
    ),
  })

  const otpauthUri = new OTPAuth.TOTP({
    issuer,
    label: name || undefined,
    secret,
    algorithm: ALGORITHMS[algorithm],
    digits: DIGITS[digits] ?? 6,
    period: 30,
  }).toString()

  return { ...base, otpauthUri }
}

/**
 * otpauth-migration URI → { accounts, batch }.
 * accounts: [{ otpauthUri, issuer, name }] — TOTP 외(HOTP/MD5/무효 secret)는
 * { issuer, name, skipped: 'hotp'|'invalid' } 로 표시만 하고 제외.
 * batch: { index, size, id } | null — Google 이 계정 수에 따라 QR 을 분할할 때 안내용.
 * 무효 입력(스킴/base64/빈 payload)은 code 를 가진 Error throw — 메시지에 입력 내용을
 * 싣지 않는다(secret 유출 방지).
 */
export function decodeMigrationUri(uri) {
  const raw = String(uri || '').trim()
  if (!raw.toLowerCase().startsWith(MIGRATION_SCHEME)) {
    throw migrationError('NOT_MIGRATION_URI')
  }

  const dataMatch = raw.match(/[?&]data=([^&]*)/)
  const encoded = dataMatch ? dataMatch[1] : ''
  if (!encoded) throw migrationError('INVALID_MIGRATION_PAYLOAD')

  let bytes
  try {
    const b64 = decodeURIComponent(encoded).replace(/ /g, '+')
    const bin = atob(b64)
    bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
  } catch (e) {
    throw migrationError('INVALID_MIGRATION_PAYLOAD')
  }
  if (bytes.length === 0) throw migrationError('INVALID_MIGRATION_PAYLOAD')

  const fields = readMessageFields(bytes)
  const paramList = fields[1] || []
  const accounts = paramList
    .filter((p) => p instanceof Uint8Array)
    .map((paramBytes) => toAccount(paramBytes))

  const batchSize = fields[3]?.[0]
  const batchIndex = fields[4]?.[0]
  const batchId = fields[5]?.[0]
  // id 기본값을 0 으로 두면 batch_id 를 생략한 서로 다른 내보내기가 같은 export 로
  // 오판된다(리뷰 A6) — 없는 값은 null 로 보존하고 비교측이 null 을 관대하게 다룬다.
  const batch =
    batchSize != null && batchSize > 0 && (batchIndex != null || batchId != null)
      ? { index: batchIndex ?? 0, size: batchSize, id: batchId ?? null }
      : null

  return { accounts, batch }
}

export default decodeMigrationUri
